import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminApi } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { selectUserRole } from '../../store/slices/authSlice';

/* ── Constants ───────────────────────────────────────────────────────────── */
const STATUS_OPTIONS = ['NEW', 'CONTACTED', 'CONVERTED', 'CLOSED'];

const STATUS_STYLES = {
  NEW:       { bg: 'bg-blue-50',   text: 'text-blue-700',   dot: 'bg-blue-500'   },
  CONTACTED: { bg: 'bg-amber-50',  text: 'text-amber-700',  dot: 'bg-amber-500'  },
  CONVERTED: { bg: 'bg-green-50',  text: 'text-green-700',  dot: 'bg-green-500'  },
  CLOSED:    { bg: 'bg-gray-100',  text: 'text-gray-500',   dot: 'bg-gray-400'   },
};

const TYPE_EMOJI = {
  'Vendor / Manufacturer': '🏭',
  'Supplier':              '📦',
  'Retailer / Distributor':'🏪',
  'Furniture Brand':       '🛋️',
  'Other inquiry':         '💬',
};

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function fmtDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/* ── StatusBadge ─────────────────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.NEW;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {status}
    </span>
  );
}

/* ── StatusDropdown ──────────────────────────────────────────────────────── */
// The menu is rendered via a portal into document.body and positioned with
// fixed coordinates computed from the trigger button. This is required
// because the table wrapper it lives in uses overflow-x-auto (for
// responsive horizontal scrolling), and CSS makes overflow-x: auto also
// clip the y-axis — an absolutely-positioned menu inside that container
// would get cut off instead of floating over the rest of the page.
function StatusDropdown({ inquiryId, current, onUpdate, isUpdating }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  function handleSelect(status) {
    setOpen(false);
    if (status !== current) onUpdate(inquiryId, status);
  }

  // Position the portal menu just under the trigger button, right-aligned to it.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setCoords({ top: rect.bottom + 4, left: rect.right });
  }, [open]);

  // Close on outside click or on scroll/resize (position would go stale otherwise).
  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const handleReposition = () => setOpen(false);
    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => !isUpdating && setOpen(o => !o)}
        disabled={isUpdating}
        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        title="Change status"
      >
        <StatusBadge status={current} />
        {isUpdating ? (
          <svg className="animate-spin h-3 w-3 text-blue-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <svg className="h-3 w-3 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        )}
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          onClick={e => e.stopPropagation()}
          style={{ position: 'fixed', top: coords.top, left: coords.left, transform: 'translateX(-100%)' }}
          className="z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[130px]"
        >
          {STATUS_OPTIONS.map(s => (
            <button
              key={s}
              onClick={() => handleSelect(s)}
              className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${s === current ? 'font-bold' : ''}`}
            >
              <StatusBadge status={s} />
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

/* ── InquiryDetailDrawer ─────────────────────────────────────────────────── */
function InquiryDetailDrawer({ inquiry, onClose, onUpdate, updatingId }) {
  if (!inquiry) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full max-w-2xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-gray-800 text-base">{inquiry.fullName}</h2>
            <p className="text-xs text-gray-500">{inquiry.workEmail}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Status</span>
            <StatusDropdown inquiryId={inquiry.id} current={inquiry.status} onUpdate={onUpdate} isUpdating={updatingId === inquiry.id} />
          </div>

          <hr className="border-gray-100" />

          {/* Company info */}
          <div className="space-y-2">
            <Row label="Company"  value={inquiry.companyName} />
            <Row label="Type"     value={`${TYPE_EMOJI[inquiry.companyType] || ''} ${inquiry.companyType}`} />
            <Row label="Email"    value={<a href={`mailto:${inquiry.workEmail}`} className="text-blue-600 hover:underline">{inquiry.workEmail}</a>} />
          </div>

          <hr className="border-gray-100" />

          {/* Message */}
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">Requirements</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-gray-50 rounded-lg p-3 border border-gray-100">{inquiry.message}</p>
          </div>

          <hr className="border-gray-100" />

          {/* Meta */}
          <div className="space-y-2">
            <Row label="Submitted"   value={fmtDate(inquiry.createdAt)} />
            {inquiry.updatedAt && <Row label="Last Updated" value={fmtDate(inquiry.updatedAt)} />}
            {inquiry.updatedBy  && <Row label="Updated By"  value={inquiry.updatedBy} />}
            {inquiry.ipAddress  && <Row label="IP Address"  value={inquiry.ipAddress} />}
            {inquiry.sourceUrl  && (
              <Row label="Source" value={
                <a href={inquiry.sourceUrl} target="_blank" rel="noreferrer"
                   className="text-blue-600 hover:underline text-xs truncate max-w-[200px] block">
                  {inquiry.sourceUrl}
                </a>
              } />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
          <a href={`mailto:${inquiry.workEmail}?subject=Re: Your OMNiDRIVE Inquiry&body=Hi ${inquiry.fullName},%0D%0A%0D%0AThank you for reaching out to OMNiDRIVE USA...`}
             className="flex items-center justify-center gap-2 w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
            </svg>
            Reply via Email
          </a>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs text-gray-500 flex-shrink-0 w-24">{label}</span>
      <span className="text-xs text-gray-800 font-medium text-right">{value}</span>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────────────────── */
export default function InquiriesPage() {
  const role = useSelector(selectUserRole);
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage]                 = useState(0);
  const [selected, setSelected]         = useState(null);
  const [updatingId, setUpdatingId]     = useState(null); // tracks which row is mid-update

  // Client-Vendor Admin has the same access as the Super Admin.
  if (!['PLATFORM_SUPER_ADMIN', 'PLATFORM_ADMIN', 'CLIENT_VENDOR_ADMIN'].includes(role)) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-400">
        <svg className="h-12 w-12 mb-3 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
        </svg>
        <p className="font-semibold">Access Restricted</p>
        <p className="text-sm mt-1">Only Platform Admins can view leads.</p>
      </div>
    );
  }

  const { data, isLoading, isError } = useQuery(
    ['inquiries', statusFilter, page],
    () => adminApi.getInquiries({ status: statusFilter || undefined, page, size: 20, sort: 'createdAt,desc' }),
    { keepPreviousData: true }
  );

  const { data: countData } = useQuery(
    ['inquiries-count-new'],
    () => adminApi.getInquiryCountNew(),
    { refetchInterval: 60_000 }
  );

  const updateMutation = useMutation(
    ({ id, status }) => adminApi.updateInquiryStatus(id, status),
    {
      onMutate: ({ id }) => setUpdatingId(id),
      onSuccess: (_, { id, status }) => {
        queryClient.invalidateQueries(['inquiries']);
        queryClient.invalidateQueries(['inquiries-count-new']);
        toast.success(`Lead marked as ${status}`);
        if (selected?.id === id) {
          setSelected(prev => ({ ...prev, status }));
        }
      },
      onError: () => toast.error('Failed to update status'),
      onSettled: () => setUpdatingId(null),
    }
  );

  const inquiries = data?.data?.content || [];
  const totalPages = data?.data?.totalPages || 0;
  const totalElements = data?.data?.totalElements || 0;
  const newCount = countData?.data ?? 0;

  if (isLoading) return <LoadingSpinner />;
  if (isError)   return <p className="text-center py-12 text-red-500">Failed to load inquiries.</p>;

  return (
    <div className="space-y-5">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            Leads &amp; Inquiries
            {newCount > 0 && (
              <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-blue-500 text-white text-xs font-bold">
                {newCount > 99 ? '99+' : newCount}
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {totalElements} total lead{totalElements !== 1 ? 's' : ''} from the public inquiry form
          </p>
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      {inquiries.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <svg className="h-12 w-12 mx-auto mb-3 text-gray-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3v-3z"/>
          </svg>
          <p className="text-gray-400 font-medium">No inquiries found</p>
          {statusFilter && (
            <button onClick={() => setStatusFilter('')} className="mt-2 text-sm text-blue-500 hover:underline">
              Clear filter
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Company</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Submitted</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {inquiries.map(inq => (
                  <tr
                    key={inq.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => setSelected(inq)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{inq.fullName}</div>
                      <div className="text-xs text-gray-400">{inq.workEmail}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{inq.companyName}</td>
                    <td className="px-4 py-3 text-gray-500 hidden md:table-cell">
                      {TYPE_EMOJI[inq.companyType] || ''} {inq.companyType}
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <StatusDropdown
                        inquiryId={inq.id}
                        current={inq.status}
                        onUpdate={(id, status) => updateMutation.mutate({ id, status })}
                        isUpdating={updatingId === inq.id}
                      />
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs hidden lg:table-cell">
                      {fmtDate(inq.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        className="text-xs text-blue-500 hover:underline"
                        onClick={e => { e.stopPropagation(); setSelected(inq); }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-500">
                Page {page + 1} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                  className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-white transition-colors"
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-white transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Detail Drawer ───────────────────────────────────────────────── */}
      {selected && (
        <InquiryDetailDrawer
          inquiry={selected}
          onClose={() => setSelected(null)}
          onUpdate={(id, status) => updateMutation.mutate({ id, status })}
          updatingId={updatingId}
        />
      )}
    </div>
  );
}
